// public/js/features/assets/asset-fields.js
// Client side of the optional asset-field registry (superadmin-managed dropdowns
// like Asset type, Engine type). Loads the definitions, resolves per-tenant
// visibility (default on) and effective label (tenant rename → default), and
// offers superadmin CRUD for the option lists.

let _cache = null;
let _ts = 0;
const TTL = 60 * 1000;

export async function loadAssetFields(force = false) {
  if (!force && _cache && Date.now() - _ts < TTL) return _cache;
  const res = await window.authenticatedFetch('api/v1/asset-fields');
  if (!res.ok) throw new Error('Failed to load asset fields');
  _cache = (await res.json()).fields || [];
  _ts = Date.now();
  return _cache;
}

export function invalidateAssetFieldsCache() { _cache = null; _ts = 0; }

// Visible to a tenant? Explicit tenant override wins, else the field default.
export function fieldVisibleForTenant(field, tenant) {
  const ov = tenant && tenant.assetFieldVisibility ? tenant.assetFieldVisibility[field.fieldId] : undefined;
  return ov === undefined ? (field.defaultEnabled !== false) : !!ov;
}

// Effective label = tenant rename → field default → fieldId.
export function effectiveFieldLabel(field, tenant) {
  const custom = tenant && tenant.assetFieldLabels ? tenant.assetFieldLabels[field.fieldId] : null;
  return (custom && String(custom).trim()) || field.label || field.fieldId;
}

export function optionLabel(field, value) {
  if (value == null || value === '') return '';
  const o = (field.options || []).find(o => o.value === value);
  return o ? o.label : value;
}

// Registry fields visible to a tenant, in display order.
export function visibleFields(fields, tenant) {
  return (fields || [])
    .filter(f => fieldVisibleForTenant(f, tenant))
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
}

// ── superadmin CRUD ──────────────────────────────────────────────────────────
export async function saveAssetFieldOptions(fieldId, options) {
  const res = await window.authenticatedFetch(`api/v1/asset-fields/${encodeURIComponent(fieldId)}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ options }),
  });
  if (!res.ok) throw new Error('Failed to save options');
  invalidateAssetFieldsCache();
  return res.json();
}

export async function patchAssetField(fieldId, patch) {
  const res = await window.authenticatedFetch(`api/v1/asset-fields/${encodeURIComponent(fieldId)}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error('Failed to save field');
  invalidateAssetFieldsCache();
  return res.json();
}
